import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	AlphaExtendedThinkingMode,
	AutoActionApprovalMode,
	RovoChatSourceMode,
	SendMessageArtifactContext,
	SendMessageBrowserContext,
	SendMessageChatContext,
	SendMessageDashboardContext,
	SendMessageDashboardEditorContext,
	SendMessageDatabaseContext,
	SendMessageEditorContext,
	SendMessageSearchArtifact,
	SendMessageSlidesContext,
	SendMessageWhiteboardContext,
	SendMessageWorkflowContext,
	WritingStyleContext,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

export type BuildMessageStreamRequestContextParams = {
	browserUrl: string;
	autoActionApprovalMode?: AutoActionApprovalMode;
	useCurrentBrowsingContext?: boolean;
	webSearchEnabled?: boolean;
	sourceMode?: RovoChatSourceMode;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
	fastModeEnabled?: boolean;
	planModeEnabled?: boolean;
	editorContext?: SendMessageEditorContext;
	whiteboardContext?: SendMessageWhiteboardContext;
	slidesContext?: SendMessageSlidesContext;
	databaseContext?: SendMessageDatabaseContext;
	dashboardEditorContext?: SendMessageDashboardEditorContext;
	browserContext?: SendMessageBrowserContext;
	/** When true, omit browser htmlBody/canvasText/selection (blocklisted URL). */
	isBrowserContextBlocklisted?: boolean;
	jiraWorkflowContext?: SendMessageWorkflowContext;
	additionalContext?: Record<string, unknown>;
	writingStyleContext?: WritingStyleContext;
	dashboardContext?: SendMessageDashboardContext;
	aiFeatureContext?: SendMessageChatContext;
	fileIds?: string[];
	knowledgeOverride?: Record<string, unknown>;
	selectedCloudIdsOverride?: Record<string, unknown>;
	searchArtifact?: SendMessageSearchArtifact;
	artifactContext?: SendMessageArtifactContext;
	shouldExtractConversationScopes?: boolean;
};

/**
 * Builds the wire `context` object for Rovo Chat message-stream requests.
 * Shared by `sendMessageStream` and Voice/EHER pull-context so payloads cannot drift.
 */
export function buildMessageStreamRequestContext(
	params: BuildMessageStreamRequestContextParams,
): Record<string, unknown> {
	const {
		browserUrl,
		autoActionApprovalMode,
		useCurrentBrowsingContext,
		webSearchEnabled,
		sourceMode,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		planModeEnabled,
		editorContext,
		whiteboardContext,
		slidesContext,
		databaseContext,
		dashboardEditorContext,
		browserContext,
		isBrowserContextBlocklisted = false,
		jiraWorkflowContext,
		additionalContext,
		writingStyleContext,
		dashboardContext,
		aiFeatureContext,
		fileIds,
		knowledgeOverride,
		selectedCloudIdsOverride,
		searchArtifact,
		artifactContext,
		shouldExtractConversationScopes,
	} = params;

	const isBrowserSelectionContextGateEnabled = fg('rovo_chat_browser_selection_context');
	const browserSelection = isBrowserSelectionContextGateEnabled
		? browserContext?.context?.selection
		: undefined;

	return {
		// without useCurrentBrowsingContext browser_url is not always used by the Rovo,
		// instead it depends on prompt intent to use it,
		// with useCurrentBrowsingContext we can ensure it is always used
		browser_url: browserUrl,
		...(autoActionApprovalMode ? { auto_action_approval_mode: autoActionApprovalMode } : {}),
		...(useCurrentBrowsingContext === undefined
			? {}
			: { useCurrentBrowsingContext: useCurrentBrowsingContext }),
		webSearchEnabled,
		...(sourceMode ? { sourceMode } : {}),
		deepResearchEnabled,
		...(thinkDeeperEnabled ? { extendedThinkingEnabled: thinkDeeperEnabled } : {}),
		...(alphaExtendedThinkingMode ? { alphaExtendedThinkingMode } : {}),
		...(fastModeEnabled ? { fastModeEnabled } : {}),
		...(planModeEnabled
			? {
					planModeEnabled,
					alphaExtendedThinkingMode: 'MARATHON',
					extendedThinkingEnabled: true,
				}
			: {}),
		...(editorContext ? { editor: editorContext } : {}),
		...(whiteboardContext ? { whiteboardContext } : {}),
		...(slidesContext ? { slidesContext } : {}),
		...(databaseContext ? { databaseContext } : {}),
		...(dashboardEditorContext ? { dashboardEditorContext } : {}),
		// If browserUrl is inaccessible, fallback to htmlBody or canvasText if they exist
		// Do not send htmlBody or canvasText if the URL is blocklisted to ensure the site is truly not sent to the backend
		...(browserContext && !isBrowserContextBlocklisted
			? {
					browser: {
						htmlBody: browserContext.context?.htmlBody,
						canvasText: browserContext.context?.canvasText,
						...(browserSelection ? { selection: browserSelection } : {}),
					},
				}
			: {}),
		jiraWorkflowContext,
		...additionalContext,
		...(writingStyleContext ? { writingStyleContext } : {}),
		dashboardContext,
		aiFeatureContext,
		fileIds,
		...knowledgeOverride,
		...selectedCloudIdsOverride,
		...(searchArtifact ? { search_artifact: searchArtifact } : {}),
		...(artifactContext ? { artifact_context: artifactContext } : {}),
		...(shouldExtractConversationScopes !== undefined ? { shouldExtractConversationScopes } : {}),
	};
}
